import { useState } from 'react';
import type { Client } from 'src/admin/users/types';
import { ClientSearchModal } from 'src/clients/components/ClientSearchModal';
import { useFamilyMeetingParticipants } from 'src/meetings/hooks/useFamilyMeetingParticipants';
import type { CalendarEvent } from 'src/meetings/models';
import { showSuccess } from 'src/notifications/notifications';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type Props = {
  opened: boolean;
  onClose: () => void;
  familyId: string;
  eventId: string;
};

export function ChooseClientModal({ opened, onClose, familyId, eventId }: Props) {
  const { addParticipant, removeParticipant } = useFamilyMeetingParticipants(familyId);
  const [addedById, setAddedById] = useState<
    Record<string, { client: Client; participantId: string | null }>
  >({});

  const selectedMembers = Object.values(addedById).map(entry => entry.client);

  async function handleToggle(client: Client) {
    const existing = addedById[client.id];

    if (existing) {
      try {
        if (existing.participantId) {
          await removeParticipant.mutateAsync({ eventId, participantId: existing.participantId });
        }
        setAddedById(previous => {
          const next = { ...previous };
          delete next[client.id];
          return next;
        });
        showSuccess('Removed');
      } catch (error) {
        notifyAndLogError(
          'Failed to remove client',
          'Could not remove the client from this meeting. Please try again.',
          error,
        );
      }
      return;
    }

    try {
      const { data: event } = await addParticipant.mutateAsync({
        eventId,
        body: { participantType: 'CLIENT', clientId: client.id },
      });
      setAddedById(previous => ({
        ...previous,
        [client.id]: { client, participantId: findClientParticipantId(event, client.id) },
      }));
      showSuccess('Added');
    } catch (error) {
      notifyAndLogError(
        'Failed to add client',
        'Could not add the client to this meeting. Please try again.',
        error,
      );
    }
  }

  return (
    <ClientSearchModal
      opened={opened}
      onClose={onClose}
      size={680}
      selectedMembers={selectedMembers}
      onToggle={handleToggle}
    />
  );
}

function findClientParticipantId(event: CalendarEvent, clientId: string): string | null {
  const match = event.eventParticipants.find(ep => ep.participant.client?.id === clientId);
  return match?.participant.id ?? null;
}
